import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarCheck, FileText, Pencil, Send, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { WORKFLOW_STYLES } from "@/pages/admin/Customers";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const WORKFLOW = ["New Enquiry", "Awaiting Payment", "Payment Received", "Booking Pending", "Booking Confirmed", "Completed", "Cancelled"];
const EVENT_TYPES = [
  { value: "enquiry", label: "Customer message" },
  { value: "reply", label: "My reply" },
  { value: "payment_link", label: "Payment link sent" },
  { value: "payment", label: "Payment received" },
  { value: "note", label: "Internal note" },
];

const fmtDT = (iso) =>
  new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "Australia/Sydney" });
const fmtD = (iso) => new Date(iso).toLocaleDateString("en-AU", { timeZone: "Australia/Sydney" });
const nowLocal = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const Bubble = ({ side, label, body, at, testid }) => (
  <div className={`flex ${side === "right" ? "justify-end" : "justify-start"}`} data-testid={testid}>
    <div className={`max-w-[80%] px-5 py-4 text-sm leading-relaxed ${
      side === "right" ? "bg-[#C5A059]/10 border border-[#C5A059]/30" : "bg-white border border-[#C5A059]/25"}`}>
      <p className="text-[9px] tracking-[0.25em] uppercase text-[#9E7B3A] mb-1.5">{label}</p>
      <p className="whitespace-pre-wrap text-[#1A1A1A]">{body}</p>
      <p className="text-[10px] text-[#4A4A4A] mt-2">{fmtDT(at)}</p>
    </div>
  </div>
);

const SystemCard = ({ tone, title, lines, at, dateOnly, testid }) => (
  <div className="flex justify-center" data-testid={testid}>
    <div className={`text-center px-6 py-4 border ${
      tone === "green" ? "border-emerald-200 bg-emerald-50/60" : "border-[#C5A059]/40 bg-[#C5A059]/5"}`}>
      <p className={`text-[10px] tracking-[0.3em] uppercase ${tone === "green" ? "text-emerald-800" : "text-[#9E7B3A]"}`}>{title}</p>
      {lines.map((l, i) => (
        <p key={i} className={`text-sm mt-1 ${i === 0 ? "font-medium text-[#1A1A1A]" : "text-[#4A4A4A] text-xs"}`}>{l}</p>
      ))}
      <p className="text-[10px] text-[#4A4A4A] mt-2">{dateOnly ? fmtD(at) : fmtDT(at)}</p>
    </div>
  </div>
);

const TimelineEvent = ({ event, customerName }) => {
  const { type, body, meta = {}, occurred_at } = event;
  if (type === "enquiry") return <Bubble side="left" label={customerName} body={body} at={occurred_at} testid={`event-${event.id}`} />;
  if (type === "reply") return <Bubble side="right" label="Australian Luxe" body={body} at={occurred_at} testid={`event-${event.id}`} />;
  if (type === "payment_link")
    return <SystemCard tone="gold" title="Payment Link Sent" testid={`event-${event.id}`} at={occurred_at}
      lines={[`$${(meta.amount ?? 0).toFixed(2)}`, meta.order_number ? `Order Reference: ${meta.order_number}` : "", body].filter(Boolean)} />;
  if (type === "payment")
    return <SystemCard tone="green" title="Payment Received" dateOnly testid={`event-${event.id}`} at={occurred_at}
      lines={[`$${(meta.amount ?? 0).toFixed(2)}${meta.order_number ? ` · Order: ${meta.order_number}` : ""}`, body || "Payment status: PAID"]} />;
  if (type === "status")
    return (
      <div className="flex justify-center" data-testid={`event-${event.id}`}>
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] border border-[#C5A059]/40 px-4 py-1.5 bg-white">
          {body} · {fmtDT(occurred_at)}
        </p>
      </div>
    );
  if (type === "invoice") return <SystemCard tone="gold" title="Invoice" lines={[body]} at={occurred_at} testid={`event-${event.id}`} />;
  if (type === "booking") return <SystemCard tone="gold" title="Booking" lines={[body]} at={occurred_at} testid={`event-${event.id}`} />;
  return (
    <div className="flex justify-center" data-testid={`event-${event.id}`}>
      <p className="text-xs italic text-[#4A4A4A] text-center max-w-md">{body} · {fmtDT(occurred_at)}</p>
    </div>
  );
};

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [eventType, setEventType] = useState("reply");
  const [body, setBody] = useState("");
  const [occurredAt, setOccurredAt] = useState(nowLocal());
  const [orderRef, setOrderRef] = useState("");
  const [amount, setAmount] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "" });
  const [bookingOpen, setBookingOpen] = useState(false);
  const [booking, setBooking] = useState({ service: "Signature Luxe Facial — 60 min", date: "", time: "", price: 119 });
  const [payLinkOpen, setPayLinkOpen] = useState(false);
  const [payLink, setPayLink] = useState({ amount: "", order_number: "" });
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState("");
  const [sendingInvoice, setSendingInvoice] = useState(false);

  const load = () => api.get(`/admin/customers/${id}`).then(({ data }) => setData(data)).catch(() => toast.error("Customer not found"));
  useEffect(() => { load(); }, [id]);

  if (!data) return <p className="text-xs tracking-[0.3em] uppercase text-[#9E7B3A]">Loading…</p>;
  const { customer, events, orders } = data;

  const addEvent = async (e) => {
    e.preventDefault();
    setAdding(true);
    const isMoney = eventType === "payment_link" || eventType === "payment";
    const payload = {
      type: eventType,
      body: body.trim(),
      occurred_at: occurredAt ? new Date(occurredAt).toISOString() : null,
      order_number: orderRef.trim() || null,
      amount: isMoney && amount !== "" ? parseFloat(amount) : null,
    };
    if (isMoney && !payload.body) {
      payload.body = eventType === "payment_link"
        ? `Payment link sent — $${(payload.amount ?? 0).toFixed(2)}${payload.order_number ? ` · Order ${payload.order_number}` : ""}`
        : "Payment status: PAID";
    }
    try {
      await api.post(`/admin/customers/${id}/events`, payload);
      toast.success("Added to timeline");
      setBody("");
      setAmount("");
      setOccurredAt(nowLocal());
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.put(`/admin/customers/${id}/status`, { status });
      toast.success(`Stage updated to ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveDetails = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/customers/${id}`, editForm);
      toast.success("Customer updated");
      setEditing(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmBooking = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/customers/${id}/booking`, { ...booking, price: parseFloat(booking.price) || 0 });
      toast.success("Booking confirmed — it's now in your Bookings page");
      setBookingOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendPaymentLink = async (e) => {
    e.preventDefault();
    const amount = parseFloat(payLink.amount);
    if (!amount || amount <= 0) { toast.error("Enter the amount"); return; }
    try {
      await api.post(`/admin/customers/${id}/events`, {
        type: "payment_link",
        body: `Here's your secure payment link 🤍 Please process $${amount.toFixed(2)} through this link: https://square.link/u/FlI5wfG1${payLink.order_number ? ` — your order reference is ${payLink.order_number}` : ""}`,
        order_number: payLink.order_number || null,
        amount,
      });
      toast.success("Payment link sent — customer notified by email");
      setPayLinkOpen(false);
      setPayLink({ amount: "", order_number: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendInvoice = async (e) => {
    e.preventDefault();
    if (!invoiceOrder) { toast.error("Choose an order"); return; }
    setSendingInvoice(true);
    try {
      await api.post(`/admin/orders/${invoiceOrder}/invoice/email`);
      toast.success("Invoice emailed — and added to the conversation");
      setInvoiceOpen(false);
      setInvoiceOrder("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingInvoice(false);
    }
  };

  return (
    <div data-testid="customer-detail-page">
      <button onClick={() => navigate("/admin/customers")} data-testid="back-to-customers"
        className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#4A4A4A] hover:text-[#9E7B3A] mb-6 transition-colors duration-300">
        <ArrowLeft className="w-4 h-4" /> All Customers
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl font-light mb-1" data-testid="customer-name">{customer.name}</h1>
          <p className="text-sm text-[#4A4A4A]">{customer.email || "No email"} {customer.phone && `· ${customer.phone}`}</p>
          <div className="flex items-center gap-3 mt-3">
            <span className={`text-[10px] tracking-widest uppercase px-2.5 py-1 ${WORKFLOW_STYLES[customer.workflow_status] || ""}`}
              data-testid="workflow-status-badge">
              {customer.workflow_status}
            </span>
            <Select value={customer.workflow_status} onValueChange={setStatus}>
              <SelectTrigger data-testid="workflow-status-select" className="rounded-none h-8 w-48 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{WORKFLOW.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => { setEditForm({ name: customer.name, email: customer.email || "", phone: customer.phone || "" }); setEditing(true); }}
            data-testid="edit-customer-button"
            className="flex items-center gap-2 border border-[#C5A059]/40 text-xs tracking-[0.2em] uppercase px-4 py-2.5 text-[#4A4A4A] hover:border-[#9E7B3A] hover:text-[#9E7B3A] transition-colors duration-300">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={() => setPayLinkOpen(true)} data-testid="send-payment-link-button"
            className="flex items-center gap-2 border border-[#C5A059]/40 text-xs tracking-[0.2em] uppercase px-4 py-2.5 text-[#4A4A4A] hover:border-[#9E7B3A] hover:text-[#9E7B3A] transition-colors duration-300">
            <CreditCard className="w-3.5 h-3.5" /> Payment Link
          </button>
          {orders.length > 0 && (
            <button onClick={() => setInvoiceOpen(true)} data-testid="send-invoice-button"
              className="flex items-center gap-2 border border-[#C5A059]/40 text-xs tracking-[0.2em] uppercase px-4 py-2.5 text-[#4A4A4A] hover:border-[#9E7B3A] hover:text-[#9E7B3A] transition-colors duration-300">
              <FileText className="w-3.5 h-3.5" /> Send Invoice
            </button>
          )}
          <button onClick={() => setBookingOpen(true)} data-testid="confirm-booking-button"
            className="flex items-center gap-2 bg-[#1A1A1A] text-white text-xs tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-[#9E7B3A] transition-colors duration-300">
            <CalendarCheck className="w-3.5 h-3.5" /> Confirm Booking
          </button>
        </div>
      </div>

      {orders.length > 0 && (
        <div className="mb-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-3">Orders</p>
          <div className="space-y-2" data-testid="customer-orders">
            {orders.map((o) => (
              <div key={o.id} className="bg-white border border-[#C5A059]/25 px-5 py-4 flex flex-wrap items-center justify-between gap-3"
                data-testid={`customer-order-${o.id}`}>
                <div className="text-sm">
                  <span className="font-medium">{o.order_number}</span>
                  <span className="text-[#4A4A4A] ml-3">${o.total?.toFixed(2)} · {o.status} · {o.payment_method}</span>
                </div>
                <Link to={`/admin/orders/${o.id}/invoice`} data-testid={`view-invoice-${o.id}`}
                  className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#9E7B3A] hover:text-[#1A1A1A] transition-colors duration-300">
                  <FileText className="w-3.5 h-3.5" /> View Invoice
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-4">Timeline</p>
      <div className="bg-[#FAF7F0] border border-[#C5A059]/25 p-6 space-y-4 mb-8" data-testid="customer-timeline">
        {events.length === 0 && <p className="text-sm text-[#4A4A4A] text-center py-6" data-testid="timeline-empty">Nothing yet — log the first message below.</p>}
        {events.map((ev) => <TimelineEvent key={ev.id} event={ev} customerName={customer.name} />)}
      </div>

      <form onSubmit={addEvent} className="bg-white border border-[#C5A059]/25 p-6 space-y-4" data-testid="add-event-form">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A]">Add to Timeline</p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase">Type</Label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger data-testid="event-type-select" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>{EVENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase">Date & Time</Label>
            <Input type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)}
              data-testid="event-datetime-input" className="rounded-none" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase">Order Ref</Label>
            <Input value={orderRef} onChange={(e) => setOrderRef(e.target.value)} placeholder="e.g. AL-1005"
              data-testid="event-order-ref-input" className="rounded-none" />
          </div>
        </div>
        {(eventType === "payment_link" || eventType === "payment") && (
          <div className="space-y-1.5 w-40">
            <Label className="text-xs tracking-widest uppercase">Amount ($)</Label>
            <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="event-amount-input" className="rounded-none" />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs tracking-widest uppercase">
            {eventType === "enquiry" ? "Customer's message" : eventType === "reply" ? "Your reply" : "Details"}
          </Label>
          <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} data-testid="event-body-input" className="rounded-none"
            placeholder={eventType === "payment_link" || eventType === "payment" ? "Optional — auto-filled if left blank" : ""} />
        </div>
        <button type="submit" disabled={adding} data-testid="event-add-submit"
          className="flex items-center gap-2 bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase px-6 py-3 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
          <Send className="w-3.5 h-3.5" /> {adding ? "Adding…" : "Add to Timeline"}
        </button>
      </form>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-md rounded-none" data-testid="edit-customer-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl font-light">Edit Customer</DialogTitle></DialogHeader>
          <form onSubmit={saveDetails} className="space-y-4 mt-2 text-sm">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Name</Label>
              <Input required value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="edit-customer-name" className="rounded-none" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="edit-customer-email" className="rounded-none" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Phone</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                data-testid="edit-customer-phone" className="rounded-none" />
            </div>
            <button type="submit" data-testid="edit-customer-submit"
              className="w-full bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase py-3.5 hover:bg-[#9E7B3A] transition-colors duration-300">
              Save Changes
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="confirm-booking-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl font-light">Confirm Booking</DialogTitle></DialogHeader>
          <form onSubmit={confirmBooking} className="space-y-4 mt-2 text-sm">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Service</Label>
              <Input required value={booking.service} onChange={(e) => setBooking((b) => ({ ...b, service: e.target.value }))}
                data-testid="booking-service-input" className="rounded-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs tracking-widest uppercase">Date</Label>
                <Input required type="date" value={booking.date} onChange={(e) => setBooking((b) => ({ ...b, date: e.target.value }))}
                  data-testid="booking-date-input" className="rounded-none" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-widest uppercase">Time</Label>
                <Input required type="time" value={booking.time} onChange={(e) => setBooking((b) => ({ ...b, time: e.target.value }))}
                  data-testid="booking-time-input" className="rounded-none" />
              </div>
            </div>
            <div className="space-y-1.5 w-40">
              <Label className="text-xs tracking-widest uppercase">Price ($)</Label>
              <Input type="number" step="0.01" min="0" value={booking.price} onChange={(e) => setBooking((b) => ({ ...b, price: e.target.value }))}
                data-testid="booking-price-input" className="rounded-none" />
            </div>
            <button type="submit" data-testid="booking-confirm-submit"
              className="w-full bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase py-3.5 hover:bg-[#9E7B3A] transition-colors duration-300">
              Confirm Booking
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={payLinkOpen} onOpenChange={setPayLinkOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="payment-link-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl font-light">Send Payment Link</DialogTitle></DialogHeader>
          <form onSubmit={sendPaymentLink} className="space-y-4 mt-2 text-sm">
            <p className="text-xs text-[#4A4A4A]">The customer is emailed and a pay card appears in their messenger.</p>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Amount ($)</Label>
              <Input required type="number" step="0.01" min="0" value={payLink.amount}
                onChange={(e) => setPayLink((p) => ({ ...p, amount: e.target.value }))}
                data-testid="paylink-amount-input" className="rounded-none" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Order Reference (optional)</Label>
              <Input value={payLink.order_number} onChange={(e) => setPayLink((p) => ({ ...p, order_number: e.target.value }))}
                data-testid="paylink-order-input" className="rounded-none" placeholder="e.g. AL-1005" />
            </div>
            <button type="submit" data-testid="paylink-send-submit"
              className="w-full bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase py-3.5 hover:bg-[#9E7B3A] transition-colors duration-300">
              Send Payment Link
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="send-invoice-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl font-light">Send Invoice</DialogTitle></DialogHeader>
          <form onSubmit={sendInvoice} className="space-y-4 mt-2 text-sm">
            <p className="text-xs text-[#4A4A4A]">Emails the branded invoice and adds it to their messenger.</p>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Order</Label>
              <Select value={invoiceOrder} onValueChange={setInvoiceOrder}>
                <SelectTrigger data-testid="invoice-order-select" className="rounded-none"><SelectValue placeholder="Choose order" /></SelectTrigger>
                <SelectContent>
                  {orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_number} — ${o.total?.toFixed(2)} ({o.status})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button type="submit" disabled={sendingInvoice} data-testid="invoice-send-submit"
              className="w-full bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase py-3.5 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
              {sendingInvoice ? "Sending…" : "Email Invoice"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
